import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { Paper, Box } from '@mui/material';

// イベントカードのスタイル（ゲーミング）
export const StyledEventCard = styled(motion.div)(({ theme, isPast }) => ({
  padding: theme.spacing(2.5),
  marginBottom: theme.spacing(2),
  backgroundColor: isPast ? 'rgba(50, 50, 60, 0.4)' : 'rgba(37, 37, 48, 0.8)',
  backdropFilter: 'blur(16px)',
  borderRadius: 0,
  boxShadow: isPast ? 'none' : '0 0 20px rgba(0, 255, 255, 0.3)',
  cursor: 'pointer',
  border: `2px solid ${isPast ? 'rgba(128, 128, 128, 0.3)' : 'rgba(0, 255, 255, 0.3)'}`,
  transition: theme.transitions.create(['all'], {
    duration: theme.transitions.duration.standard,
    easing: theme.transitions.easing.easeInOut,
  }),
  position: 'relative',
  overflow: 'hidden',
  color: isPast ? 'rgba(255, 255, 255, 0.5)' : theme.palette.text.primary,
  opacity: isPast ? 0.7 : 1,
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '2px',
    background: `linear-gradient(90deg, transparent, ${theme.palette.neon.cyan}, transparent)`,
    transition: 'left 0.5s ease',
  },
  
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    right: '-100%',
    width: '100%',
    height: '2px',
    background: `linear-gradient(90deg, transparent, ${theme.palette.neon.magenta}, transparent)`,
    transition: 'right 0.5s ease',
  },
  
  '&:hover': {
    transform: 'translateY(-4px) scale(1.02)',
    boxShadow: isPast ? '0 0 15px rgba(128, 128, 128, 0.4)' : '0 0 30px rgba(0, 255, 255, 0.5)',
    borderColor: isPast ? 'rgba(128, 128, 128, 0.5)' : theme.palette.neon.cyan,
    backgroundColor: isPast ? 'rgba(50, 50, 60, 0.6)' : 'rgba(0, 255, 255, 0.05)',
    opacity: isPast ? 0.85 : 1,
    
    '&::before': {
      left: '100%',
    },
    '&::after': {
      right: '100%',
    },
  },
  
  '&:active': {
    transform: 'translateY(0) scale(1)',
    boxShadow: 'inset 0 0 20px rgba(0, 255, 255, 0.2)',
  },
  
  // モバイル対応
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
  },
}));

// カレンダーの日付コンテナ（ゲーミング）
export const StyledCalendarDay = styled(motion.div)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  padding: theme.spacing(2),
  borderRadius: 0,
  backgroundColor: 'transparent',
  transition: theme.transitions.create(['all'], {
    duration: theme.transitions.duration.short,
  }),
  position: 'relative',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '3px',
    height: '100%',
    background: 'linear-gradient(180deg, transparent, rgba(0, 255, 255, 0.5), transparent)',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  
  '.day-number': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  
  '.date-circle': {
    width: '48px',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 0,
    backgroundColor: 'rgba(37, 37, 48, 0.8)',
    border: `2px solid rgba(0, 255, 255, 0.3)`,
    transition: theme.transitions.create(['all'], {
      duration: theme.transitions.duration.standard,
    }),
    fontWeight: 700,
    fontSize: '1.1rem',
    position: 'relative',
    overflow: 'hidden',
    
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '-2px',
      left: '-2px',
      right: '-2px',
      bottom: '-2px',
      background: 'linear-gradient(45deg, #00ffff, #ff00ff, #00ff00, #00ffff)',
      opacity: 0,
      transition: 'opacity 0.3s ease',
      filter: 'blur(5px)',
    },
    
    [theme.breakpoints.down('sm')]: {
      width: '40px',
      height: '40px',
      fontSize: '1rem',
    },
  },
  
  '&[data-is-today="true"]': {
    '&::before': {
      opacity: 1,
    },
    
    '.date-circle': {
      background: 'linear-gradient(135deg, rgba(0, 255, 255, 0.2), rgba(255, 0, 255, 0.2))',
      color: theme.palette.neon.cyan,
      borderColor: theme.palette.neon.cyan,
      boxShadow: `0 0 20px ${theme.palette.neon.cyan}`,
      transform: 'scale(1.05)',
      
      '&::before': {
        opacity: 0.5,
      },
    },
  },
  
  '&:hover': {
    backgroundColor: 'rgba(0, 255, 255, 0.05)',
    
    '&::before': {
      opacity: 1,
    },
    
    '.date-circle': {
      transform: 'scale(1.1) rotate(5deg)',
      boxShadow: '0 0 15px rgba(0, 255, 255, 0.5)',
      borderColor: theme.palette.neon.cyan,
    },
  },
  
  // モバイル対応
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(1.5),
  },
}));

// グラスモーフィズムペーパー（ゲーミング）
export const GlassPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: 'rgba(37, 37, 48, 0.8)',
  backdropFilter: 'blur(20px)',
  border: `2px solid rgba(0, 255, 255, 0.2)`,
  boxShadow: '0 0 30px rgba(0, 255, 255, 0.3)',
  transition: theme.transitions.create(['all'], {
    duration: theme.transitions.duration.standard,
  }),
  borderRadius: 0,
  position: 'relative',
  overflow: 'hidden',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, transparent, rgba(0, 255, 255, 0.5), transparent)',
    animation: 'scan 3s linear infinite',
  },
  
  '@keyframes scan': {
    '0%': { transform: 'translateX(-100%)' },
    '100%': { transform: 'translateX(100%)' },
  },
  
  '&:hover': {
    backgroundColor: 'rgba(37, 37, 48, 0.9)',
    boxShadow: '0 0 40px rgba(0, 255, 255, 0.5)',
    borderColor: 'rgba(0, 255, 255, 0.4)',
  },
}));

// フローティングアクションボタン（ゲーミング）
export const FloatingActionButton = styled(motion.div)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(3),
  right: theme.spacing(3),
  zIndex: theme.zIndex.fab,
  
  '& button': {
    background: 'linear-gradient(45deg, #00ffff, #ff00ff)',
    border: '2px solid transparent',
    boxShadow: '0 0 30px rgba(0, 255, 255, 0.5)',
    
    '&:hover': {
      transform: 'scale(1.1) rotate(180deg)',
      boxShadow: '0 0 40px rgba(255, 0, 255, 0.7)',
    },
  },
  
  [theme.breakpoints.down('sm')]: {
    bottom: theme.spacing(2),
    right: theme.spacing(2),
  },
}));

// ヘッダーコンテナ（ゲーミング）
export const StyledHeader = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  backgroundColor: 'rgba(26, 26, 31, 0.95)',
  backdropFilter: 'blur(20px)',
  borderBottom: `2px solid rgba(0, 255, 255, 0.2)`,
  zIndex: theme.zIndex.appBar,
  padding: theme.spacing(2),
  boxShadow: '0 0 30px rgba(0, 255, 255, 0.3)',
  transition: theme.transitions.create(['all'], {
    duration: theme.transitions.duration.standard,
  }),
  
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-2px',
    left: 0,
    right: 0,
    height: '2px',
    background: 'linear-gradient(90deg, #00ffff, #ff00ff, #00ff00, #00ffff)',
    backgroundSize: '200% 100%',
    animation: 'gradient-shift 3s linear infinite',
  },
  
  '@keyframes gradient-shift': {
    '0%': { backgroundPosition: '0% 50%' },
    '100%': { backgroundPosition: '200% 50%' },
  },
  
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
  },
}));

// モーダルコンテナ（ゲーミング）
export const StyledModal = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: '700px',
  maxHeight: '85vh',
  overflow: 'hidden',
  backgroundColor: 'rgba(37, 37, 48, 0.95)',
  borderRadius: 0,
  border: '2px solid rgba(0, 255, 255, 0.3)',
  boxShadow: '0 0 50px rgba(0, 255, 255, 0.5)',
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-2px',
    left: '-2px',
    right: '-2px',
    bottom: '-2px',
    background: 'linear-gradient(45deg, #00ffff, #ff00ff, #00ff00, #00ffff)',
    opacity: 0.5,
    filter: 'blur(10px)',
    zIndex: -1,
  },
  
  '&:focus': {
    outline: 'none',
  },
  
  [theme.breakpoints.down('sm')]: {
    position: 'fixed',
    top: 0,
    left: 0,
    transform: 'none',
    width: '100%',
    height: '100%',
    maxHeight: '100%',
    maxWidth: '100%',
    borderRadius: 0,
  },
}));

// スケルトンローダー（ゲーミング）
export const SkeletonCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  marginBottom: theme.spacing(2),
  borderRadius: 0,
  backgroundColor: 'rgba(37, 37, 48, 0.6)',
  border: '2px solid rgba(0, 255, 255, 0.1)',
  position: 'relative',
  overflow: 'hidden',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(0, 255, 255, 0.2), transparent)',
    animation: 'skeleton-scan 1.5s linear infinite',
  },
  
  '@keyframes skeleton-scan': {
    '0%': { left: '-100%' },
    '100%': { left: '100%' },
  },
  
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
  },
}));

// アニメーションバリアント（ゲーミング）
export const fadeInUp = {
  initial: {
    opacity: 0,
    y: 30,
    filter: 'blur(10px)',
  },
  animate: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 0.6,
      ease: [0.25, 0.1, 0.25, 1],
    },
  },
  exit: {
    opacity: 0,
    y: -30,
    filter: 'blur(10px)',
    transition: {
      duration: 0.4,
      ease: [0.4, 0, 1, 1],
    },
  },
};

export const scaleIn = {
  initial: {
    opacity: 0,
    scale: 0.8,
    rotate: -5,
  },
  animate: {
    opacity: 1,
    scale: 1,
    rotate: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.1, 0.25, 1],
      opacity: { duration: 0.3 },
      scale: { duration: 0.5 },
      rotate: { duration: 0.5 },
    },
  },
  exit: {
    opacity: 0,
    scale: 0.8,
    rotate: 5,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 1, 1],
    },
  },
};

export const slideIn = {
  initial: (direction) => ({
    x: direction === 'left' ? 400 : -400,
    opacity: 0,
    scale: 0.8,
  }),
  animate: {
    x: 0,
    opacity: 1,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 20,
      mass: 0.8,
    },
  },
  exit: (direction) => ({
    x: direction === 'left' ? -400 : 400,
    opacity: 0,
    scale: 0.8,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 20,
      mass: 0.8,
    },
  }),
};

// カスタムスクロールバー（ゲーミング）
export const customScrollbar = {
  '&::-webkit-scrollbar': {
    width: '10px',
    height: '10px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#1a1a1f',
    border: '1px solid rgba(0, 255, 255, 0.1)',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'linear-gradient(45deg, #00ffff, #ff00ff)',
    borderRadius: '0',
    '&:hover': {
      background: 'linear-gradient(45deg, #66ffff, #ff66ff)',
      boxShadow: '0 0 10px rgba(0, 255, 255, 0.5)',
    },
  },
};
